
import { defineComponent, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import moment from 'moment';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import { showToastError, showToastSuccess } from '@/helpers';
import { UserState } from '@/composables/User';
import { setRefreshScenariosList, updateCampaignBulk } from '@/composables/AutomatedScenarios';
import { getBulkCampaignStatus } from '@/composables/workflows/CampaignBulk';
import { CampaignsBulkStatusEnum } from '@/types';
import type { CampaignsBulkUpdateInputItem } from '@/types/generated-types/graphql';
import type { CampaignActionPayload, ArchiveCampaignResult } from './types';

export default defineComponent({
  name: 'ArchiveBulkCampaignAction',

  components: {
    Dialog,
    SpmButton,
  },

  emits: ['confirm'],

  setup(_, { emit, expose }) {
    const { t } = useI18n();
    const idShop = ref<number>(UserState.activeShop?.id ?? 0);

    // Internal state
    const isVisible = ref(false);
    const isLoading = ref(false);
    const currentPayload = ref<CampaignActionPayload | null>(null);

    /**
     * Open the dialog with the given payload
     */
    const open = (payload: CampaignActionPayload) => {
      currentPayload.value = payload;
      isVisible.value = true;
    };

    /**
     * Close the dialog
     */
    const close = () => {
      isVisible.value = false;
      currentPayload.value = null;
      isLoading.value = false;
    };

    /**
     * Confirm the action and emit result
     */
    const confirm = async () => {
      if (!currentPayload.value) return;

      isLoading.value = true;

      try {
        // Check if status hasn't changed on server
        const currentStatus = await getBulkCampaignStatus(currentPayload.value.campaignId);
        if (!currentStatus || currentStatus !== currentPayload.value.campaignStatus) {
          await showToastError(t('errorMessages.GENERIC_ERROR'));
          setRefreshScenariosList(true);
          close();
          return;
        }

        const input: CampaignsBulkUpdateInputItem = {
          id_campaign_bulk: currentPayload.value.campaignId,
          id_shop: idShop.value,
          status: CampaignsBulkStatusEnum.ARCHIVED_BY_USER,
          date_modification: moment().format('YYYY-MM-DD HH:mm:ss'),
        };

        await updateCampaignBulk([input]);
        await showToastSuccess(t('campaignsBulkList.actions.archiveSuccess'));
        setRefreshScenariosList(true);

        const result: ArchiveCampaignResult = {
          campaignId: currentPayload.value.campaignId,
        };
        emit('confirm', result);
        close();
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : t('errorMessages.GENERIC_ERROR');
        await showToastError(errorMessage);
      } finally {
        isLoading.value = false;
      }
    };

    // Expose open method for parent component
    expose({ open });

    return {
      t,
      isVisible,
      isLoading,
      close,
      confirm,
    };
  },
});
